import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { CallOverlay } from "@/components/chat/CallOverlay";
import { startRingtone } from "@/components/chat/ringtone";

const STUN = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };
const END_NOTICE = { reject: "Call declined", busy: "They're on another call", unavailable: "They're offline — we let them know you called", cancel: "Missed call", end: "Call ended" };

// One-to-one voice/video calls over WebRTC, signalled through the chat socket.
export default function CallManager() {
    const { subscribe, send, callRef } = useChat();
    const [call, setCall] = useState(null);
    const [localStream, setLocal] = useState(null);
    const [remoteStream, setRemote] = useState(null);
    const cur = useRef(null);
    const pc = useRef(null);
    const media = useRef(null);
    const pending = useRef([]);
    const timer = useRef(null);
    const ring = useRef(null);
    const ice = useRef(STUN);

    useEffect(() => {
        api.get("/chat/ice-servers").then(({ data }) => { ice.current = { iceServers: data.ice_servers }; }).catch(() => {});
    }, []);

    const update = (c) => { cur.current = c; setCall(c); };
    const stopRing = () => { ring.current?.(); ring.current = null; };
    const signal = useCallback((payload) => {
        const c = cur.current;
        if (c) send({ type: "call", to: c.peer.id, payload: { ...payload, call_id: c.id } });
    }, [send]);

    const cleanup = useCallback((notice) => {
        stopRing();
        clearTimeout(timer.current);
        pc.current?.close();
        pc.current = null;
        media.current?.getTracks().forEach((t) => t.stop());
        media.current = null;
        pending.current = [];
        setLocal(null);
        setRemote(null);
        update(null);
        if (notice) toast(notice);
    }, []);

    const getMedia = async (kind) => {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: kind === "video" });
        media.current = s;
        setLocal(s);
    };

    const makePc = () => {
        const p = new RTCPeerConnection(ice.current);
        p.onicecandidate = (e) => e.candidate && signal({ action: "ice", candidate: e.candidate });
        p.ontrack = (e) => setRemote(e.streams[0]);
        p.onconnectionstatechange = () => {
            if (p.connectionState === "connected" && cur.current) update({ ...cur.current, state: "active", startedAt: Date.now() });
            if (p.connectionState === "failed") { signal({ action: "end" }); cleanup("Call could not connect on this network"); }
        };
        media.current.getTracks().forEach((t) => p.addTrack(t, media.current));
        pc.current = p;
        return p;
    };

    const flushIce = async () => {
        for (const c of pending.current) await pc.current.addIceCandidate(c).catch(() => {});
        pending.current = [];
    };

    callRef.current = async (peer, kind) => {
        if (cur.current) return toast("Finish your current call first");
        update({ id: crypto.randomUUID(), peer, kind, dir: "out", state: "ringing" });
        try { await getMedia(kind); } catch { return cleanup("Allow microphone/camera access to call"); }
        signal({ action: "invite", kind });
        timer.current = setTimeout(() => { signal({ action: "cancel" }); cleanup(`${peer.name} didn't answer`); }, 35000);
    };

    const accept = async () => {
        stopRing();
        clearTimeout(timer.current);
        try { await getMedia(cur.current.kind); } catch { signal({ action: "reject" }); return cleanup("Allow microphone/camera access to answer"); }
        makePc();
        update({ ...cur.current, state: "connecting" });
        signal({ action: "accept" });
    };
    const decline = () => { signal({ action: "reject" }); cleanup(); };
    const hangup = () => { signal({ action: cur.current?.state === "ringing" ? "cancel" : "end" }); cleanup(); };

    useEffect(() => subscribe(async (m) => {
        if (m.type !== "call") return;
        const { action, call_id: id } = m.payload || {};
        const c = cur.current;
        if (action === "invite") {
            if (c) return send({ type: "call", to: m.from, payload: { action: "busy", call_id: id } });
            update({ id, peer: { id: m.from, name: m.from_name }, kind: m.payload.kind === "video" ? "video" : "audio", dir: "in", state: "incoming" });
            ring.current = startRingtone();
            timer.current = setTimeout(() => cleanup(`Missed call from ${m.from_name}`), 35000);
            return;
        }
        if (!c || c.id !== id) return;
        try {
            if (action === "accept" && c.dir === "out") {
                clearTimeout(timer.current);
                update({ ...c, state: "connecting" });
                const p = makePc();
                await p.setLocalDescription(await p.createOffer());
                signal({ action: "offer", sdp: p.localDescription });
            } else if (action === "offer" && pc.current) {
                await pc.current.setRemoteDescription(m.payload.sdp);
                await flushIce();
                await pc.current.setLocalDescription(await pc.current.createAnswer());
                signal({ action: "answer", sdp: pc.current.localDescription });
            } else if (action === "answer" && pc.current) {
                await pc.current.setRemoteDescription(m.payload.sdp);
                await flushIce();
            } else if (action === "ice") {
                if (pc.current?.remoteDescription) await pc.current.addIceCandidate(m.payload.candidate).catch(() => {});
                else pending.current.push(m.payload.candidate);
            } else if (END_NOTICE[action]) {
                cleanup(action === "cancel" ? `Missed call from ${c.peer.name}` : END_NOTICE[action]);
            }
        } catch {
            signal({ action: "end" });
            cleanup("Call failed to connect");
        }
    }), [subscribe, send, signal, cleanup]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => cleanup(), [cleanup]);

    if (!call) return null;
    return <CallOverlay call={call} localStream={localStream} remoteStream={remoteStream} onAccept={accept} onDecline={decline} onHangup={hangup} />;
}
